// HistoryDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { format } from 'date-fns';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { History, Clock, ThumbsDown, RotateCcw } from 'lucide-react';

/**
 * Dialog for viewing and reverting deliverable revision history
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.deliverableHistory - History data with current and past versions
 * @param {function} props.onRevert - Callback when revert is clicked (receives version number)
 * @param {function} props.onClose - Callback when close is clicked
 */
export const HistoryDialog = ({
  open,
  onOpenChange,
  deliverableHistory,
  onRevert,
  onClose
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-blue-500" />
            Revision History
          </DialogTitle>
          <DialogDescription>
            View all previous versions of this deliverable. Last 5 revisions are kept.
          </DialogDescription>
        </DialogHeader>
        
        {deliverableHistory && (
          <div className="space-y-4 py-4">
            {/* Current Version */}
            <Card className="border-emerald-200 bg-emerald-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Badge className="bg-emerald-500">Current</Badge>
                    Version {deliverableHistory.current?.version || 1}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {deliverableHistory.current?.timestamp 
                      ? format(new Date(deliverableHistory.current.timestamp), 'PPpp')
                      : 'Now'}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deliverableHistory.current?.base64 ? (
                  <div className="aspect-video bg-white rounded overflow-hidden max-w-[300px]">
                    <img 
                      src={`data:image/png;base64,${deliverableHistory.current.base64}`}
                      alt="Current version"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="bg-white rounded p-3 max-h-[150px] overflow-y-auto">
                    <p className="text-sm whitespace-pre-wrap">
                      {deliverableHistory.current?.content?.substring(0, 300) || 'No content'}...
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Previous Versions */}
            {deliverableHistory.history?.length > 0 && (
              <div>
                <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Previous Versions
                </h4>
                <div className="space-y-3">
                  {deliverableHistory.history.slice().reverse().map((revision, idx) => (
                    <Card 
                      key={idx}
                      className={`${
                        revision.status === 'rejected' ? 'border-red-200 bg-red-50' :
                        revision.status === 'superseded' ? 'border-slate-200' :
                        'border-amber-200 bg-amber-50'
                      }`}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <Badge 
                              variant="outline"
                              className={
                                revision.status === 'rejected' ? 'border-red-300 text-red-700' :
                                revision.status === 'superseded' ? 'border-slate-300 text-slate-700' :
                                'border-amber-300 text-amber-700'
                              }
                            >
                              {revision.status === 'rejected' && <ThumbsDown className="h-3 w-3 mr-1" />}
                              {revision.status}
                            </Badge>
                            Version {revision.version}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(revision.timestamp), 'PPpp')}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onRevert(revision.version)}
                              data-testid={`revert-version-${revision.version}`}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Revert
                            </Button>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {revision.feedback_received && (
                          <p className="text-xs text-muted-foreground mb-2">
                            <strong>Feedback:</strong> {revision.feedback_received}
                          </p>
                        )}
                        {revision.rejection_reason && (
                          <p className="text-xs text-red-600 mb-2">
                            <strong>Reason:</strong> {revision.rejection_reason}
                          </p>
                        )}
                        {revision.base64 ? (
                          <div className="aspect-video bg-white rounded overflow-hidden max-w-[200px]">
                            <img 
                              src={`data:image/png;base64,${revision.base64}`}
                              alt={`Version ${revision.version}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="bg-white rounded p-2 max-h-[80px] overflow-y-auto">
                            <p className="text-xs whitespace-pre-wrap">
                              {revision.content?.substring(0, 150) || 'No content'}...
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            
            {(!deliverableHistory.history || deliverableHistory.history.length === 0) && (
              <div className="text-center py-8">
                <History className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">No revision history yet</p>
                <p className="text-sm text-muted-foreground">
                  History will be recorded when you generate variations or make changes
                </p>
              </div>
            )}
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="history-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HistoryDialog;
